import { clone } from "./types";
import type { Draft, Row, Values } from "./types";
export interface Collision { path: string; local: unknown; remote: unknown }
function equal(a: unknown, b: unknown): boolean {
    a ??= false; b ??= false;
    if (a === b) return true;
    if (Array.isArray(a) || Array.isArray(b)) return Array.isArray(a) && Array.isArray(b)
        && a.length === b.length && a.every((value, index) => equal(value, b[index]));
    if (typeof a !== "object" || typeof b !== "object") return false;
    const left = a as Record<string, unknown>, right = b as Record<string, unknown>;
    const keys = Object.keys(left);
    return keys.length === Object.keys(right).length && keys.every(key => Object.hasOwn(right, key) && equal(left[key], right[key]));
}
export function mergeDraft(base: Draft, local: Draft, remote: Draft, choices: Record<string, "local" | "remote"> = {}) {
    const conflicts: Collision[] = [];
    function pick(path: string, before: unknown, mine: unknown, theirs: unknown): unknown {
        if (equal(mine, before)) return clone(theirs);
        if (equal(theirs, before) || equal(mine, theirs)) return clone(mine);
        if (!choices[path]) conflicts.push({ path, local: mine, remote: theirs });
        return clone(choices[path] === "remote" ? theirs : mine);
    }
    function values(path: string, before: Values, mine: Values, theirs: Values): Values {
        return Object.fromEntries([...new Set([...Object.keys(before), ...Object.keys(mine), ...Object.keys(theirs)])]
            .map(key => [key, pick(`${path}.${key}`, before[key], mine[key], theirs[key])])) as Values;
    }
    const result: Draft = { header: values("header", base.header, local.header, remote.header), tables: {} };
    for (const table of new Set([...Object.keys(base.tables), ...Object.keys(local.tables), ...Object.keys(remote.tables)])) {
        const before = new Map((base.tables[table] ?? []).map(row => [row.key, row]));
        const mine = new Map((local.tables[table] ?? []).map(row => [row.key, row]));
        const theirs = new Map((remote.tables[table] ?? []).map(row => [row.key, row]));
        const rows: Row[] = [];
        for (const key of new Set([...theirs.keys(), ...mine.keys(), ...before.keys()])) {
            const b = before.get(key), l = mine.get(key), r = theirs.get(key);
            if (b && l && r) rows.push({ ...r, values: values(`${table}.${key}`, b.values, l.values, r.values) });
            else {
                const selected = pick(`${table}.${key}`, b, l, r) as Row | undefined;
                if (selected) rows.push({ ...selected, id: r?.id ?? null });
            }
        }
        result.tables[table] = rows;
    }
    return { draft: result, conflicts };
}
export function remap(draft: Draft, metadata: import("./types").Snapshot["tables"], maps: Record<string, Record<string, string>>): Draft {
    const result = clone(draft);
    for (const [table, rows] of Object.entries(result.tables)) {
        for (const row of rows) {
            row.key = maps[table]?.[row.key] ?? row.key;
            if (/^r-\d+$/.test(row.key)) row.id = Number(row.key.slice(2));
            for (const field of metadata[table]?.fields ?? []) {
                if (field.table_ref && typeof row.values[field.name] === "string") {
                    row.values[field.name] = maps[field.table_ref]?.[row.values[field.name] as string] ?? row.values[field.name] as string;
                }
            }
        }
    }
    return result;
}
